import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import style from './style.module.less';
import { InfoIcon } from 'ui-components/components/info-icon';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

/**
 * Rate shopper option component for selecting a rate shopping strategy
 * @typedef {object} RateShopperOptionProps
 * @property {string} id - Unique identifier for the option
 * @property {object} title - Formatted message object for the option title
 * @property {object} description - Formatted message object for the option description
 * @property {boolean} isSelected - Whether this option is currently selected
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} onSelect - Callback function when this option is selected
 * @property {boolean} [showDefaultBadge=false] - Whether to show the default badge
 * @property {string} value - Value associated with this option
 * @property {object} [defaultBadgeText=null] - Formatted message object for the default badge text
 * @property {React.ReactNode} [children=null] - Optional content rendered below the option when selected
 * @property {object} [tooltipText] - Formatted message object for the info icon tooltip text
 */

/**
 * @param {RateShopperOptionProps} props
 * @returns {JSX.Element}
 */
export const RateShopperOption = ({
  id,
  title,
  description,
  isSelected,
  onSelect,
  showDefaultBadge = false,
  value,
  defaultBadgeText = null,
  tooltipText,
  children = null,
}) => {
  const intl = useIntl();
  return (
    <FlexColumn
      className={classnames(style['rate-shopper-option'], {
        [style.selected]: isSelected,
      })}
    >
      <FlexRow className={style['rate-shopper-option-row']}>
        <RadioCheckListItem className={style['rate-shopper-checkbox']} id={id} isChecked={isSelected} onChecked={onSelect} value={value}>
          <FlexColumn className={style['rate-shopper-option-content']}>
            <FlexRow gap={Gap.Space8}>
              <Text className={style['rate-shopper-option-title']} textType={TextType.Paragraph} value={title} />
              {tooltipText && <InfoIcon color={THEME_CONSTANTS.unstyled} size='small' tooltipText={intl.formatMessage(tooltipText)} />}
            </FlexRow>
            <Text className={style['rate-shopper-option-description']} textType={TextType.Paragraph} value={description} />
          </FlexColumn>
        </RadioCheckListItem>
        {showDefaultBadge && (
          <div className={style['default-badge']}>
            <Text value={defaultBadgeText} />
          </div>
        )}
      </FlexRow>
      {isSelected && children}
    </FlexColumn>
  );
};
